<template>
    <div>
        <limitless-page-header title="Kunder">
            <template #header-elements>
                <limitless-page-header-button :route="{ name: 'customers.edit', params: { id: 0 } }" icon="icon-plus3">Ny kund</limitless-page-header-button>
            </template>
        </limitless-page-header>

        <div class="content">
            <div v-if="create_order" class="alert alert-info text-center font-weight-semibold">Välj kund för att skapa order</div>
            <rednax-datatable
                :url="'/vue/customers'"
                :columns="table_columns"
                :filter="table_filter"
                @updateFilter="updateFilter"
                @rowclick="rowClick"
                :per-page="10"
                history
                sort-by="CustomerID"
                sort-order="desc"
                size="md"
            >
                <form action="" method="post" @submit.prevent="formSubmit">
                    <div class="row">
                        <div class="col-lg-2">
                            <input type="text" class="form-control" v-model="local_filter.search" placeholder="Sök...">
                        </div>
                        <div class="col-lg-3">
                            <customer-group-filter
                                v-model="local_filter.customer_group_ids"
                                :reduce="(opt) => opt.id"
                                placeholder="Välj kundgrupper"
                                multiple
                            ></customer-group-filter>
                        </div>
                        <div class="col-lg-3">
                            <seller-filter
                                v-model="local_filter.seller_ids"
                                :reduce="(opt) => opt.id"
                                placeholder="Välj säljare"
                                multiple
                            ></seller-filter>
                        </div>
                        <div class="col-lg-3">
                            <customer-status-filter
                                v-model="local_filter.status_ids"
                                :reduce="(opt) => opt.id"
                                placeholder="Välj status"
                                multiple
                            ></customer-status-filter>
                        </div>
                        <div class="col-lg-1">
                            <button type="submit" class="btn btn-primary btn-block" @click="formSubmit">Sök</button>
                        </div>
                    </div>
                </form>

                <template #cell_CustomerID="{ row }">
                    <router-link v-if="create_order" :to="{ name: 'cardorders.edit', params: { id: 0 }, query: { customer_id: row.CustomerID } }">{{ row.CustomerID }}</router-link>
                    <router-link v-else :to="{ name: 'customers.edit', params: { id: row.CustomerID } }">{{ row.CustomerID }}</router-link>
                </template>
                <template #cell_CustomerStatusID="{ row }">
                    <span :class="'badge badge-' + row.StatusClass">{{ row.StatusName }}</span>
                </template>
                <template #cell_crm_companies="{row}">
                    <i v-if="row.crm_companies && row.crm_companies.length > 0" :id="'crm_companies-' + row.CustomerID" class="icon-checkmark"></i>
                    <b-popover v-if="row.crm_companies && row.crm_companies.length > 0" :key="'crm_companies-' + row.CustomerID" :target="'crm_companies-' + row.CustomerID" triggers="hover focus" placement="bottom">
                        <div v-for="crm_company in row.crm_companies" :key="'crm_companies-' + row.CustomerID + '-' + crm_company.id">
                            <router-link :to="{ name: 'crm.company', params: { id: crm_company.id } }">{{ crm_company.name }}</router-link>
                        </div>
                    </b-popover>
                </template>
            </rednax-datatable>
        </div> <!-- /content -->
    </div>
</template>

<script>
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import LimitlessPageHeaderButton from '@components/Layout/partials/LimitlessPageHeaderButton';
import RednaxDatatable from '@components/Datatable/RednaxDatatableComponent';
import SellerFilter from '@components/Filters/SellerFilter.vue';
import CustomerGroupFilter from '@components/Filters/CustomerGroupFilter.vue';
import CustomerStatusFilter from '@components/Filters/CustomerStatusFilter.vue';
import { hasRole } from '@services/Helpers';

export default {
    name: 'CustomerList',
    components: {
        CustomerStatusFilter,
        CustomerGroupFilter,
        SellerFilter,
        LimitlessPageHeader,
        LimitlessPageHeaderButton,
        RednaxDatatable,
    },
    data: () => ({
        create_order: false,
        local_filter: {
            search: '',
            customer_group_ids: [],
            seller_ids: [],
            status_ids: [],
        },
        table_filter: {
            search: '',
            customer_group_ids: [],
            seller_ids: [],
            status_ids: [],
        },
    }),
    mounted() {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const createorder = urlParams.get('createorder');
        if (createorder) {
            this.create_order = createorder;
        }
    },
    methods: {
        rowClick(row, event) {
            if (this.create_order) {
                this.$router.push({ name: 'cardorders.edit', params: { id: 0 }, query: { customer_id: row.CustomerID } });
            } else if (event.metaKey === true || event.ctrlKey === true) {
                const route = this.$router.resolve({ name: 'customers.edit', params: { id: row.CustomerID } });
                window.open(route.href, '_blank').focus();
            } else {
                this.$router.push({ name: 'customers.edit', params: { id: row.CustomerID } });
            }
        },
        formSubmit() {
            Object.keys(this.local_filter).forEach((key) => {
                this.table_filter[key] = this.local_filter[key];
            });
        },
        updateFilter(filter) {
            Object.keys(filter).forEach((key) => {
                this.table_filter[key] = filter[key];
                this.local_filter[key] = filter[key];
            });
        },
    },
    computed: {
        table_columns() {
            const items = [
                {
                    header: 'Kundnr',
                    data: 'CustomerID',
                    sortable: true,
                },
                {
                    header: 'Status',
                    data: 'CustomerStatusID',
                    sortable: true,
                },
                {
                    header: 'Kund',
                    data: 'CompanyName',
                    sortable: true,
                },
                {
                    header: 'Kontaktperson',
                    data: 'ContactName',
                    sortable: true,
                },
                {
                    header: 'Grupp',
                    data: 'CustomerGroups',
                    sortable: true,
                },
                {
                    header: 'Säljare',
                    data: 'SellerName',
                    sortable: true,
                },
            ];

            if (hasRole('red_sales_crm_user')) {
                items.push({
                    header: 'CRM',
                    data: 'crm_companies',
                    sortable: true,
                });
            }

            return items;
        },
    },
};

</script>
